import { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import AppContext from "../../utils/AppContext";
import { api } from "../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import { setLanguage } from "../../locale/localeUtils";

import Navigation from "../../common/Navigation/Navigation";
import LiveUpdates from "../../common/Leads/LiveUpdates";
import WelcomeModal from "../Onboarding/WelcomeModal";

import './AuthRoute.scss';

/**
 * @typedef {Object} AuthRouteProps
 * @property {JSX.Element} element - Component usually a route to be rendered (usually a route)
 * @property {boolean} [navigation=false] - Show the main left-hand-side navigation (optional; defaults to false)
 * @property {boolean} [liveUpdates=false] - Connect to the websocket that allows live updates (defaults to false)
 * @property {boolean} [noRedirect] - If set to true, the component will not redirect in case the user is not logged in (defaults to false)
 * @property {boolean} [superUserOnly] - Only super-users can see this route
 * @property {boolean} [publicOnly] - Only public users can see this route, meaning logged in users will not see it (for example, /login)
 * @property {boolean} [onboardingVisible] - When enabled, users that haven't finished the onboarding process (!user.onboarding.done) will be able to see this route
 */

/**
 * Auhenticated Route
 * @param {AuthRouteProps} props - Configuration for the component
 * @returns 
 */
export default function AuthRoute({ element, navigation, liveUpdates, noRedirect = false, superUserOnly, publicOnly, onboardingVisible }) {

  const [state, dispatch] = useContext(AppContext);
  const navigate = useNavigate();

  const { loggedIn, currentUser } = state;
  const [showWelcome, setShowWelcome] = useState(false);

  const isSuperUser = currentUser?.super_user;

  const { lang } = useParams();

  useEffect(() => {
    if (!loggedIn) {
      setLanguage(lang || 'en');
    }
  }, [lang, loggedIn]);

  useEffect(() => {
    // If user is not logged in, redirect:
    if (loggedIn === false && !noRedirect && !publicOnly) {

      if (window.location.pathname !== '/') {
        navigate('/login?redirect_to=' + encodeURIComponent(window.location.pathname) + encodeURIComponent(window.location.search));
      }
      else {
        navigate('/login');
      }
    }
    // If user is logged in, redirect if the route is for "public" (non-logged-in) users only:
    else if (loggedIn === true && publicOnly) {
      navigate('/');
    }
  }, [loggedIn, noRedirect, publicOnly, navigate]);

  useEffect(() => {
    if (superUserOnly && loggedIn && !isSuperUser) {
      navigate('/')
    }
  }, [superUserOnly, loggedIn, isSuperUser, navigate]);

  const onboardingDone = currentUser?.onboarding?.done;

  const showWelcomeMessage = currentUser?.onboarding?.show_welcome;

  useEffect(() => {
    if (loggedIn && !onboardingDone && !onboardingVisible) {
      navigate('/onboarding');
    }
    else if (loggedIn && showWelcomeMessage) {
      setShowWelcome(true);
    }
  }, [loggedIn, onboardingDone, showWelcomeMessage, onboardingVisible, navigate]);

  useEffect(() => {
    const getUser = async () => {
      if (!Cookies.get('pl__api_access_token')) {
        dispatch({ type: 'loggedIn/set', payload: false });
      }
      else {
        try {
          const { data } = await api.get('/users/me/');
          dispatch({ type: 'currentUser/set', payload: data });

          setLanguage(data.settings.language);
        }
        catch {
          // if there is an issue fetching the current user, redirect to landing page:
          dispatch({ type: 'loggedIn/set', payload: false });
        }
      }
    }

    if (!currentUser) {
      getUser();
    }

  }, [currentUser, dispatch]);

  const renderElement = () => {
    if (navigation) {
      return (
        <div className='__auth-route-navigation'>
          <Navigation />
          <main className='main-content'>{ element }</main>
        </div>
      );
    }

    return element;
  }

  // for AuthRoutes, wait until loggedIn is set to true or false before rendering the element:
  if (loggedIn === null) {
    return null;
  }

  // if the user is logged in but route is public-facing only, do not render the element:
  if (loggedIn === true && publicOnly) {
    return null;
  }

  if (loggedIn && !onboardingDone && !onboardingVisible) {
    return false;
  }

  // if it's for super users only, but user is not super user, do not render it:
  if (superUserOnly && !isSuperUser) {
    return null;
  }

  // if the user is NOT logged in, do not show the content (if noRedirect and publicOnly are not set)
  if (loggedIn === false && !noRedirect && !publicOnly) {
    return null;
  }

  return (
    <>
      { renderElement() }
      { liveUpdates && <LiveUpdates /> }
      { loggedIn && onboardingDone && <WelcomeModal show={showWelcome} onHide={() => setShowWelcome(false)} /> }
    </>
  )
}
